import { Link } from '@inertiajs/react';

import Badge from '@/components/public/ui/Badge';
import Icon from '@/components/public/ui/Icon';
import { trackUmamiEvent } from '@/lib/analytics';

interface ServiceCardProps {
  title: string;
  description: string;
  icon: string;
  href: string;
  color: 'primary' | 'secondary' | 'tertiary';
  soon?: boolean;
}

const colorConfig: Record<
  ServiceCardProps['color'],
  { iconBg: string; iconText: string; edge: string }
> = {
  primary: { iconBg: 'bg-primary/12', iconText: 'text-primary', edge: 'group-hover:bg-primary' },
  secondary: {
    iconBg: 'bg-secondary/15',
    iconText: 'text-secondary',
    edge: 'group-hover:bg-gold-leaf',
  },
  tertiary: {
    iconBg: 'bg-tertiary/12',
    iconText: 'text-tertiary',
    edge: 'group-hover:bg-tertiary',
  },
};

// Light catalogue card. Whole card clickable; brand-coloured edge slides in on
// hover (same micro-interaction as the home quick-access grid).
export default function ServiceCard({
  title,
  description,
  icon,
  href,
  color,
  soon,
}: ServiceCardProps) {
  const config = colorConfig[color];

  return (
    <Link
      href={href}
      onClick={() => trackUmamiEvent('service_card_click', { service: title, target: href, soon })}
      className="group surface-card hover:border-line-strong relative flex h-full flex-col overflow-hidden p-6 transition-all duration-200 hover:shadow-md"
    >
      <span
        className={`absolute inset-y-0 left-0 w-[3px] bg-transparent transition-colors duration-150 ${config.edge}`}
      />
      {soon && (
        <Badge label="Brevemente" color="neutral" size="sm" className="absolute top-4 right-4" />
      )}
      <div
        className={`mb-4 flex h-11 w-11 items-center justify-center rounded-lg ${config.iconBg}`}
        aria-hidden="true"
      >
        <span className="text-2xl leading-none">{icon}</span>
      </div>
      <h3 className="font-headline text-on-surface text-lg font-semibold">{title}</h3>
      <p className="text-on-surface-light mt-1.5 flex-1 text-sm leading-relaxed">{description}</p>
      <span className="text-primary mt-4 inline-flex items-center gap-1.5 text-sm font-semibold">
        Mais informações
        <Icon
          name="arrow_forward"
          size="sm"
          className="transition-transform group-hover:translate-x-1"
        />
      </span>
    </Link>
  );
}
